import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tab,
  Tabs,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  AccountingActionCategory,
  AccountingActionPreferenceSelect,
  ActionCategoryMapping,
} from "@/features/chart-of-account-preferences";
import {
  ProductCategorySelect,
  ProductCategoryTypeEnum,
  ProductCategoryTypeLabels,
} from "@/features/product-categories";
import { apiClient, StatusEnum, statusLabels } from "@/features/shared";
import {
  CenteredLoading,
  CustomDialogTitle,
  CustomInputField,
  CustomSelect,
} from "@/features/ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./product-category-form-constants";

const GOODS_AND_SERVICES_ACTIONS =
  ActionCategoryMapping[AccountingActionCategory.GOODS_AND_SERVICES];

export const ProductCategoryFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const [tabIndex, setTabIndex] = useState(0);
  const [coaOptionsByAction, setCoaOptionsByAction] = useState({});
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);

  const hasParent = watch("hasParent");

  const lft = watch("lft");
  const rgt = watch("rgt");
  const isLeaf = lft != null && rgt != null ? rgt - lft === 1 : true;

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  useEffect(() => {
    const fetchPreferences = async () => {
      setIsLoadingPreferences(true);
      try {
        const {
          data: { data },
        } = await apiClient.get("accounting-action-preferences");

        const optionsByAction = {};
        for (const pref of data) {
          if (GOODS_AND_SERVICES_ACTIONS.includes(pref.action)) {
            if (!optionsByAction[pref.action]) {
              optionsByAction[pref.action] = [];
            }
            if (pref.chartOfAccount) {
              optionsByAction[pref.action].push(pref.chartOfAccount);
            }
          }
        }
        setCoaOptionsByAction(optionsByAction);
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    fetchPreferences();
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (formData) => {
    await onSubmit({
      ...formData,
      parentId: formData.parentProductCategory?.id || null,
      accountingActionChartOfAccounts: Object.entries(
        formData.accountingActionChartOfAccounts,
      )
        .filter(([, coa]) => coa != null)
        .map(([action, coa]) => ({
          action,
          chartOfAccountId: coa.id,
        })),
    });
  };

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="md">
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <DialogContent sx={{ pt: 0 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
              sx={{ mb: 0 }}
            >
              <Tab label="Kategori Barang" />
              {isLeaf && <Tab label="Akun" />}
            </Tabs>
            <Divider sx={{ mb: 2 }} />

            {tabIndex === 0 && (
              <Grid container spacing={2.5}>
                <Grid size={12}>
                  <CustomInputField
                    required
                    name="name"
                    label="Nama"
                    register={register}
                    helperText={errors.name?.message}
                    error={Boolean(errors.name)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={control}
                    name="hasParent"
                    render={({ field, fieldState }) => (
                      <CustomSelect
                        required
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          if (value === false) {
                            setValue("parentProductCategory", null);
                          }
                        }}
                        id="hasParent"
                        label="Merupakan Sub"
                        options={[
                          { value: true, label: "Ya" },
                          { value: false, label: "Tidak" },
                        ]}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={control}
                    name="parentProductCategory"
                    render={({ field, fieldState }) => (
                      <ProductCategorySelect
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!hasParent}
                        isLeaf={false}
                        excludedTreeNode={{
                          lft,
                          rgt,
                        }}
                        TextFieldProps={{
                          required: hasParent,
                          label: "Turunan dari",
                          error: fieldState.invalid,
                          helperText: fieldState.error?.message,
                          InputProps: {
                            placeholder: "Pilih Kategori Produk",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={control}
                    name="type"
                    render={({ field }) => (
                      <CustomSelect
                        required
                        id="type"
                        label="Tipe"
                        options={Object.values(ProductCategoryTypeEnum).map(
                          (value) => ({
                            value: value,
                            label: ProductCategoryTypeLabels[value],
                          }),
                        )}
                        value={field.value}
                        onChange={field.onChange}
                        helperText={errors.type?.message}
                        error={Boolean(errors.type)}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field, fieldState }) => (
                      <CustomSelect
                        required
                        value={field.value}
                        onChange={field.onChange}
                        id="status"
                        label="Status"
                        options={Object.values(StatusEnum).map((value) => ({
                          value: value,
                          label: statusLabels[value],
                        }))}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}

            {isLeaf && tabIndex === 1 && (
              <Grid container spacing={2.5}>
                {isLoadingPreferences ? (
                  <Grid size={12}>
                    <CenteredLoading />
                  </Grid>
                ) : (
                  GOODS_AND_SERVICES_ACTIONS.map((action) => (
                    <Grid size={{ xs: 12, md: 6 }} key={action}>
                      <Controller
                        control={control}
                        name={`accountingActionChartOfAccounts.${action}`}
                        render={({ field }) => (
                          <AccountingActionPreferenceSelect
                            action={action}
                            value={field.value}
                            onChange={field.onChange}
                            options={coaOptionsByAction[action] || []}
                          />
                        )}
                      />
                    </Grid>
                  ))
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};

ProductCategoryFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
